import Box from '@mui/material/Box';
import React from 'react';

import { colors } from './KestrixDesignSystem.jsx';

/**
 * KestrixDivider - A flexible divider component for all use cases
 * 
 * @param {Object} props - Component props
 * @param {string} [props.orientation] - Orientation: 'horizontal', 'vertical' (default: 'horizontal')
 * @param {string} [props.variant] - Variant: 'light', 'medium', 'dark', 'primary', 'secondary' (default: 'light')
 * @param {string} [props.color] - Custom color override (overrides variant)
 * @param {number} [props.thickness] - Thickness in pixels (default: 1)
 * @param {string} [props.width] - Width for horizontal divider (default: '100%')
 * @param {string} [props.height] - Height for vertical divider (default: '100%')
 * @param {number} [props.margin] - Margin around divider in pixels (default: 16)
 * @param {string} [props.marginX] - Horizontal margin override
 * @param {string} [props.marginY] - Vertical margin override
 * @param {Object} [props.sx] - Additional MUI sx props
 * @returns {JSX.Element} Divider component
 */
const KestrixDivider = ({ 
  orientation = 'horizontal',
  variant = 'light',
  color,
  thickness = 1,
  width,
  height,
  margin = 16,
  marginX,
  marginY,
  sx = {},
  ...props 
}) => {
  const getColor = () => {
    if (color) return color;
    
    switch (variant) {
      case 'medium': return colors.border.medium;
      case 'dark': return colors.border.dark;
      case 'primary': return colors.base.KestrixPrimary;
      case 'secondary': return colors.base.KestrixSecondary;
      case 'thermal': return colors.base.KestrixThermalMode;
      default: return colors.border.light;
    }
  };

  const getWidth = () => {
    if (width) return width;
    return orientation === 'horizontal' ? '100%' : `${thickness}px`;
  };

  const getHeight = () => {
    if (height) return height;
    return orientation === 'vertical' ? '100%' : `${thickness}px`;
  };

  const getMargin = () => {
    if (orientation === 'vertical') {
      return marginX ? `0 ${marginX}` : `0 ${margin}px`;
    }
    return marginY ? `${marginY} 0` : `${margin}px 0`;
  };

  return (
    <Box
      sx={{
        width: getWidth(),
        height: getHeight(),
        backgroundColor: getColor(),
        margin: getMargin(),
        alignSelf: orientation === 'vertical' ? 'stretch' : 'auto',
        ...sx
      }}
      {...props}
    />
  );
};

export default KestrixDivider;
